"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ActorQuerySourceDereferenceLinkHypermedia = void 0;
const bus_query_source_dereference_link_1 = require("@comunica/bus-query-source-dereference-link");
const context_entries_1 = require("@comunica/context-entries");
const core_1 = require("@comunica/core");
const readable_stream_1 = require("readable-stream");
const QuerySourceCachePolicyDereferenceWrapper_1 = require("./QuerySourceCachePolicyDereferenceWrapper");
/**
 * A comunica Dereference Query Source Hypermedia Resolve Actor.
 */
class ActorQuerySourceDereferenceLinkHypermedia extends bus_query_source_dereference_link_1.ActorQuerySourceDereferenceLink {
    mediatorDereferenceRdf;
    mediatorMetadata;
    mediatorMetadataExtract;
    mediatorMetadataAccumulate;
    mediatorQuerySourceIdentifyHypermedia;
    sparqlServiceDescriptionTimeout;
    constructor(args) {
        super(args);
        this.mediatorDereferenceRdf = args.mediatorDereferenceRdf;
        this.mediatorMetadata = args.mediatorMetadata;
        this.mediatorMetadataExtract = args.mediatorMetadataExtract;
        this.mediatorMetadataAccumulate = args.mediatorMetadataAccumulate;
        this.mediatorQuerySourceIdentifyHypermedia = args.mediatorQuerySourceIdentifyHypermedia;
        this.sparqlServiceDescriptionTimeout = args.sparqlServiceDescriptionTimeout ?? 3000;
    }
    async test(_action) {
        return (0, core_1.passTestVoid)();
    }
    getDereferenceRdfContext(context, url, forceSourceType) {
        const isSparqlServiceDescriptionDereference = forceSourceType === 'sparql' ||
            (!forceSourceType && (url.endsWith('/sparql') || url.endsWith('/sparql/')));
        if (this.sparqlServiceDescriptionTimeout > 0 &&
            isSparqlServiceDescriptionDereference) {
            const existingTimeout = context.get(context_entries_1.KeysHttp.httpTimeout);
            const effectiveTimeout = existingTimeout !== undefined && existingTimeout > 0 ?
                Math.min(existingTimeout, this.sparqlServiceDescriptionTimeout) :
                this.sparqlServiceDescriptionTimeout;
            if (effectiveTimeout === existingTimeout) {
                return context;
            }
            return context.set(context_entries_1.KeysHttp.httpTimeout, effectiveTimeout);
        }
        return context;
    }
    async run(action) {
        const context = action.link.context ? action.context.merge(action.link.context) : action.context;
        let url = action.link.url;
        let quads;
        let metadata;
        let cachePolicy;
        try {
            const dereferenceRdfContext = this.getDereferenceRdfContext(context, url, action.link.forceSourceType);
            const dereferenceRdfOutput = await this.mediatorDereferenceRdf
                .mediate({ context: dereferenceRdfContext, url });
            url = dereferenceRdfOutput.url;
            if (dereferenceRdfOutput.cachePolicy) {
                cachePolicy = new QuerySourceCachePolicyDereferenceWrapper_1.QuerySourceCachePolicyDereferenceWrapper(dereferenceRdfOutput.cachePolicy);
            }
            // Determine the metadata
            const rdfMetadataOutput = await this.mediatorMetadata.mediate({ context, url, quads: dereferenceRdfOutput.data, triples: dereferenceRdfOutput.metadata?.triples });
            rdfMetadataOutput.data.on('error', () => {
                // Silence errors in the data stream,
                // as they will be emitted again in the metadata stream,
                // and will result in a promise rejection anyways.
                // If we don't do this, we end up with an unhandled error message
            });
            metadata = (await this.mediatorMetadataExtract.mediate({
                context,
                url,
                // The problem appears to be conflicting metadata keys here
                metadata: rdfMetadataOutput.metadata,
                headers: dereferenceRdfOutput.headers,
                requestTime: dereferenceRdfOutput.requestTime,
            })).metadata;
            quads = rdfMetadataOutput.data;
            // Transform quads if needed.
            if (action.link.transform) {
                quads = await action.link.transform(quads);
            }
        }
        catch (error) {
            // Make sure that dereference errors are only emitted once an actor really needs the read quads
            // This allows SPARQL endpoints that error on service description fetching to still be source-forcible
            quads = new readable_stream_1.Readable();
            quads.read = () => {
                setTimeout(() => quads.emit('error', error));
                return null;
            };
            ({ metadata } = await this.mediatorMetadataAccumulate.mediate({ context, mode: 'initialize' }));
            // Log as warning, because the quads above may not always be consumed (e.g. for SPARQL endpoints),
            // so the user would not be notified of something going wrong otherwise.
            this.logWarn(context, `Metadata extraction for ${action.link.url} failed: ${error.message}`);
        }
        // Determine the source
        const { source, dataset } = await this.mediatorQuerySourceIdentifyHypermedia.mediate({
            context,
            forceSourceType: action.link.forceSourceType,
            handledDatasets: action.handledDatasets,
            metadata,
            quads,
            url,
        });
        if (dataset && action.handledDatasets) {
            // Mark the dataset as applied
            // This is needed to make sure that things like QPF search forms are only applied once,
            // and next page links are followed after that.
            action.handledDatasets[dataset] = true;
        }
        // Track dereference event
        context.get(context_entries_1.KeysStatistics.dereferencedLinks)?.updateStatistic({ url: action.link.url, metadata }, source);
        return { source, metadata: metadata, dataset, cachePolicy };
    }
}
exports.ActorQuerySourceDereferenceLinkHypermedia = ActorQuerySourceDereferenceLinkHypermedia;
//# sourceMappingURL=ActorQuerySourceDereferenceLinkHypermedia.js.map